import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateThreadTitle } from "@/hooks/useThreads.tsx";

/**
 * How a title being edited looks, on every surface that can edit one.
 *
 * Nothing: no border, no fill, no focus ring, and the same box the resting
 * title occupied. What marks the field as live is the selection sitting on the
 * text — the whole title is selected the moment it opens — read against the
 * card's own white surface. Chrome of any kind competes with that, and an
 * underline or a fill also changes the title's footprint, which nudges the
 * layout at the exact moment the user is aiming at it.
 *
 * Type size and leading are inherited rather than named, so the field is always
 * exactly the title it replaced. Naming them would mean tracking three separate
 * answers: the row's title is 14px from its column, the card's heading is 16px
 * from the document, and `text-base` is 14px in this system — so the one class
 * that sounds like "the normal size" is the wrong size on two of the three
 * surfaces, and the title would jump the moment editing opened. Weight is still
 * per-surface, since only the row varies it (threads needing attention are
 * bold).
 *
 * The `md:` duplicates are load-bearing, not restatements. `Input`'s own base
 * classes carry `md:text-sm`, and tailwind-merge keys on the modifier — so an
 * unmodified class cannot displace a modified one. Drop them and the card's
 * 16px title silently drops to 14px past `md` the moment editing opens, which
 * is the one thing this constant exists to prevent.
 */
export const inlineRenameFieldClass =
  "h-auto rounded-none border-0 bg-transparent px-0 py-0 text-[length:inherit] leading-[inherit] md:text-[length:inherit] md:leading-[inherit] focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0";

/**
 * The inline-rename state machine, shared by every surface a thread's title can
 * be edited on: the detail header, the list row, and the card.
 *
 * Editing is driven from outside rather than owned here. On every surface but
 * the header, the control that starts a rename is the overflow menu, which is
 * not the thing that renders the title — so the caller holds the boolean and
 * this hook owns the part that is genuinely shared: the draft, and the rules
 * for when a draft becomes a title.
 *
 * Enter commits, Escape reverts, and losing focus commits. Committing on blur
 * is the deliberate choice of the three: an edit box that silently discarded
 * what you typed because you clicked elsewhere is the one outcome a user cannot
 * anticipate. An empty value clears the title, which hands naming back to the
 * server — auto-title-gen refills it on the next agent turn.
 */
export const useInlineRename = ({
  threadId,
  title,
  isEditing,
  onDone,
}: {
  threadId: number;
  title: string | null | undefined;
  isEditing: boolean;
  /** Called once the edit settles, either way, so the caller can close it. */
  onDone: () => void;
}) => {
  const [draft, setDraft] = useState(title ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Set when Enter or Escape drove the outcome, so the blur that follows is a
  // no-op. Without it, Escape on a dirty draft would still reach `onBlur` and
  // commit the text it had just reverted, and Enter would fire the mutation
  // twice.
  const settledByKeyRef = useRef(false);
  const { mutate: updateTitle } = useUpdateThreadTitle({ threadId });

  // Seed the draft when editing opens, during render rather than in an effect,
  // so the input's first paint already holds the current title instead of
  // flashing the previous edit's text. (React's documented way to adjust state
  // when a prop changes.) It matters here because these components are recycled
  // across threads as a list scrolls: a draft left over from the last open would
  // arrive holding some other thread's name.
  const [editingWas, setEditingWas] = useState(isEditing);
  if (isEditing !== editingWas) {
    setEditingWas(isEditing);
    if (isEditing) setDraft(title ?? "");
  }

  // Select rather than place a caret: typing then replaces the whole name,
  // which is what renaming a title the server wrote almost always means.
  //
  // `title` is deliberately absent from the dependencies — re-running this
  // mid-edit would reselect the field out from under the user every time a
  // socket update touched the thread.
  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const commit = useCallback(() => {
    const next = draft.trim();
    if (next !== (title ?? "")) updateTitle(next);
    onDone();
  }, [draft, title, updateTitle, onDone]);

  const onBlur = useCallback(() => {
    if (settledByKeyRef.current) {
      settledByKeyRef.current = false;
      return;
    }
    commit();
  }, [commit]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        settledByKeyRef.current = true;
        commit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        // Keep the cancel keystroke from also reaching panel-level Escape
        // handlers, which would take the user back to the list.
        e.stopPropagation();
        settledByKeyRef.current = true;
        onDone();
      }
    },
    [commit, onDone],
  );

  /** Spread onto an `<Input>`; each surface supplies its own `className`. */
  return {
    inputProps: {
      ref: inputRef,
      type: "text" as const,
      value: draft,
      maxLength: 200,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value),
      onBlur,
      onKeyDown,
      "aria-label": "Edit thread title",
    },
  };
};
